import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link, useParams } from 'react-router-dom';
import NavBar from '../components/navigation';
import Footer from '../components/Footer';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Fuel,
  Gauge,
  Mail,
  MessageCircle,
  MapPin,
  Phone,
  Share2,
  Send,
  LockKeyhole,
  X,
} from 'lucide-react';
import { fetchCar, recordCarView } from '../api/cars';
import { createContactRequest } from '../api/contactRequests';
import { getMarketplaceToken, getMarketplaceUser } from '../api/marketplaceAuth';
import { updatePageMetadata } from '../components/SEO';

const CURRENCY_LABELS = { USD: '$', KES: 'Ksh ' };

const formatPrice = (value, currency = 'KES') => {
  const label = CURRENCY_LABELS[currency] ?? `${currency} `;
  return `${label}${Number(value || 0).toLocaleString('en-US')}`;
};

const formatValue = (value) => {
  if (!value) return 'Not provided';
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
};

function DetailItem({ label, value }) {
  return (
    <div className="border-b border-border py-3">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-1 text-sm font-semibold text-dark">
        {formatValue(value)}
      </dd>
    </div>
  );
}

function FeatureTag({ name }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-dark">
      {name}
    </span>
  );
}

function ImageGallery({ images, name }) {
  const [activeImage, setActiveImage] = useState(0);

  const prev = () =>
    setActiveImage((activeImage - 1 + images.length) % images.length);
  const next = () =>
    setActiveImage((activeImage + 1) % images.length);

  return (
    <section>
      <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-gray-100">
        <img
          src={images[activeImage]}
          alt={name}
          className="h-full w-full object-cover"
        />
        {images.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous image"
              onClick={prev}
              className="absolute left-4 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-dark shadow"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Next image"
              onClick={next}
              className="absolute right-4 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-dark shadow"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
          {images.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-1.5 rounded-full transition-colors ${
                i === activeImage ? 'bg-white' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </div>
      {images.length > 1 && (
        <div className="mt-3 grid grid-cols-4 gap-3 sm:grid-cols-5">
          {images.map((image, index) => (
            <button
              type="button"
              key={`${image}-${index}`}
              onClick={() => setActiveImage(index)}
              className={`aspect-[4/3] overflow-hidden rounded-lg border-2 transition-colors ${
                activeImage === index
                  ? 'border-[#c1392a]'
                  : 'border-transparent'
              }`}
            >
              <img
                src={image}
                alt={`${name} view ${index + 1}`}
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function CarSummaryPanel({ car, name }) {
  const adNumber = car.adNumber || car.id;

  return (
    <section className="rounded-2xl border border-border bg-white p-6 sm:p-8">
      {car.badge && (
        <span className="inline-flex rounded-full bg-[#c1392a] px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
          {car.badge}
        </span>
      )}
      <h1 className="mt-3 font-heading text-3xl font-bold text-dark sm:text-4xl">
        {name}
      </h1>
      <p className="mt-3 font-heading text-2xl font-bold text-[#c1392a]">
        {formatPrice(car.price, car.currency)}
      </p>

      <dl className="mt-5 space-y-2 text-sm">
        {car.location && (
          <div className="flex items-center justify-between border-b border-border py-2">
            <dt className="text-muted">Location</dt>
            <dd className="flex items-center gap-1 font-medium text-dark">
              <MapPin className="h-3.5 w-3.5 text-[#c1392a]" />
              {car.location}
            </dd>
          </div>
        )}
        {car.year && (
          <div className="flex items-center justify-between border-b border-border py-2">
            <dt className="text-muted">Year</dt>
            <dd className="font-medium text-dark">{car.year}</dd>
          </div>
        )}
        {car.mileage != null && (
          <div className="flex items-center justify-between border-b border-border py-2">
            <dt className="flex items-center gap-1 text-muted">
              <Gauge className="h-3.5 w-3.5 text-[#c1392a]" /> Mileage
            </dt>
            <dd className="font-medium text-dark">
              {Number(car.mileage).toLocaleString()} km
            </dd>
          </div>
        )}
        {car.fuelType && (
          <div className="flex items-center justify-between border-b border-border py-2">
            <dt className="flex items-center gap-1 text-muted">
              <Fuel className="h-3.5 w-3.5 text-[#c1392a]" /> Fuel
            </dt>
            <dd className="font-medium text-dark">{formatValue(car.fuelType)}</dd>
          </div>
        )}
      </dl>
    </section>
  );
}

function VehicleDetailsPanel({ car }) {
  return (
    <section className="rounded-2xl border border-border bg-white p-6 sm:p-8">
      <h2 className="font-heading text-xl font-bold text-dark">
        Vehicle Details
      </h2>
      <dl className="mt-4 grid gap-x-8 sm:grid-cols-2">
        <DetailItem label="Condition" value={car.condition} />
        <DetailItem label="Body type" value={car.bodyType} />
        <DetailItem label="Transmission" value={car.transmission} />
        <DetailItem label="Seats" value={car.seats} />
      </dl>
    </section>
  );
}

function DescriptionPanel({ description }) {
  return (
    <section className="rounded-2xl border border-border bg-white p-6 sm:p-8">
      <h2 className="font-heading text-xl font-bold text-dark">Description</h2>
      <p className="mt-4 whitespace-pre-line text-sm leading-7 text-muted">
        {description || 'No description provided.'}
      </p>
    </section>
  );
}

function FeaturesPanel({ features }) {
  if (features.length === 0) return null;
  return (
    <section className="rounded-2xl border border-border bg-white p-6 sm:p-8">
      <h2 className="font-heading text-xl font-bold text-dark">Extras</h2>
      <div className="mt-4 flex flex-wrap gap-2">
        {features.map((feature) => (
          <FeatureTag key={feature} name={feature} />
        ))}
      </div>
    </section>
  );
}

function InspectionReportPanel({ report }) {
  if (!report) return null;
  const values = [
    ['Interior condition', report.interiorCondition],
    ['Exterior condition', report.exteriorCondition],
    ['Accident history', report.accidentHistory],
    ['Wheel / tyre wear', report.wheelWear],
    ['Engine condition', report.engineCondition],
    ['Cooling system', report.coolingSystem],
    ['Electrical system', report.electricalSystem],
    ['Engine system', report.engineSystem],
    ['Starting system', report.startingSystem],
    ['Charging system', report.chargingSystem],
    ['Transmission system', report.transmissionSystem],
    ['Brake system', report.brakeSystem],
    ['Steering system', report.steeringSystem],
    ['Fuel system', report.fuelSystem],
  ].filter(([, value]) => value);
  const powerFeatures = Object.entries(report.powerFeatures || {}).filter(([, value]) => value);
  return (
    <section className="rounded-2xl border border-[#f3d9df] bg-[#fff5f7] p-6 sm:p-8">
      <h2 className="font-heading text-xl font-bold text-dark">Full inspection summary report</h2>
      <div className="mt-4 grid gap-3 border-y border-[#f3d9df] py-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
        {[
          ['Report date', report.reportDate],
          ['Valid until', report.validUntil],
          ['Number plate', report.plateNumber && <span className="blur-sm select-none">{report.plateNumber}</span>],
          ['VIN', report.vinNumber && <span className="blur-sm select-none">{report.vinNumber}</span>],
        ].filter(([, value]) => value).map(([label, value]) => <div key={label}><dt className="text-xs text-muted">{label}</dt><dd className="mt-1 font-semibold text-dark">{value}</dd></div>)}
      </div>
      {(report.makeModelYear || report.engineCc) && <div className="grid gap-4 py-5 sm:grid-cols-2"><DetailItem label="Make, model and year" value={report.makeModelYear} /><DetailItem label="Engine cc" value={report.engineCc} /></div>}
      {powerFeatures.length > 0 && <div className="mt-2 rounded-xl bg-white/70 p-4"><h3 className="text-sm font-bold text-dark">Power features</h3><div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">{powerFeatures.map(([key, value]) => <div key={key}><p className="text-xs font-semibold capitalize text-dark">{key.replace(/([A-Z])/g, ' $1')}</p><p className="mt-1 text-xs text-muted">{value}</p></div>)}</div></div>}
      {report.additionalFeatures && <p className="mt-4 text-sm text-muted"><strong className="text-dark">Additional features:</strong> {report.additionalFeatures}</p>}
      {report.infotainmentFeatures && <p className="mt-2 text-sm text-muted"><strong className="text-dark">Infotainment:</strong> {report.infotainmentFeatures}</p>}
      {values.length > 0 && <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{values.map(([label, value]) => <div key={label} className="rounded-lg bg-white/70 p-3"><p className="text-xs font-semibold text-dark">{label}</p><p className="mt-1 whitespace-pre-line text-xs leading-5 text-muted">{value}</p></div>)}</div>}
      <p className="mt-6 rounded-xl bg-white/70 p-4 text-xs italic leading-5 text-muted"><strong className="not-italic text-dark">Disclaimer: </strong>{report.disclaimer || 'This inspection report is a general assessment and is not a warranty or guarantee of the vehicle condition. Buyers should independently inspect and verify the vehicle before purchase.'}</p>
    </section>
  );
}

function ContactSellerModal({ car, onClose }) {
  const user = getMarketplaceUser();
  const [form, setForm] = useState({ fullName: user?.fullName || '', phone: user?.phone || '', message: 'Is it still available?', agreedTerms: false, agreedPrivacy: false, marketingConsent: true });
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  async function submit(event) {
    event.preventDefault(); setError('');
    try { await createContactRequest({ listingId: car.id, listingType: car.type || 'vehicle', ...form }); setSent(true); }
    catch (requestError) { setError(requestError.message); }
  }
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="contact-seller-title">
    <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
      <div className="flex items-center justify-between bg-[#830019] px-6 py-5 text-white"><div className="flex items-center gap-3"><span className="rounded-full bg-white/15 p-2"><MessageCircle className="h-4 w-4" /></span><h2 id="contact-seller-title" className="text-sm font-bold uppercase tracking-[0.15em]">Message the seller</h2></div><button onClick={onClose} aria-label="Close"><X /></button></div>
      {sent ? <div className="p-8 text-center"><h3 className="font-heading text-xl font-bold text-dark">Message sent</h3><p className="mt-2 text-sm text-muted">The seller can reply through their portal, phone, or email.</p><button onClick={onClose} className="mt-6 rounded-lg bg-[#c1392a] px-5 py-3 font-semibold text-white">Done</button></div> : <form onSubmit={submit} className="grid gap-4 p-6"><p className="text-sm text-muted">Your message goes straight to the seller. Replies land in your Chat tab and your email.</p><p className="text-xs font-bold uppercase tracking-wide text-muted">Start with</p><div className="flex flex-wrap gap-2">{['Is it still available?', 'Can I come and see it?', 'Is the price negotiable?'].map((draft) => <button key={draft} type="button" onClick={() => setForm({ ...form, message: draft })} className={`rounded-full border px-3 py-2 text-sm ${form.message === draft ? 'border-[#c1392a] bg-[#fff1e4]' : 'border-border'}`}>{draft}</button>)}</div><label className="grid gap-2 text-sm font-semibold text-dark">Your message<textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows="3" className="rounded-lg border border-border px-4 py-3 font-normal" /></label><div className="flex gap-2 rounded-lg bg-[#fff3df] p-3 text-xs leading-5 text-dark"><LockKeyhole className="mt-0.5 h-4 w-4 shrink-0" />Your account details will be shared with the seller so they can respond.</div><label className="flex gap-2 text-sm text-dark"><input type="checkbox" required checked={form.agreedTerms} onChange={(e) => setForm({ ...form, agreedTerms: e.target.checked })} />I agree to the <a className="font-semibold text-[#c1392a] underline" href="/terms">Terms & Conditions</a>.</label><label className="flex gap-2 text-sm text-dark"><input type="checkbox" required checked={form.agreedPrivacy} onChange={(e) => setForm({ ...form, agreedPrivacy: e.target.checked })} />I agree to the <a className="font-semibold text-[#c1392a] underline" href="/privacy">Privacy Policy</a>.</label>{error && <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}<button className="rounded-lg bg-[#c1392a] px-5 py-3 font-semibold text-dark"><Send className="mr-2 inline h-4 w-4" />Send message</button></form>}
    </div>
  </div>;
}

function LoadingState() {
  return (
    <main className="min-h-[60vh] px-6 py-24 text-center text-muted">
      Loading vehicle...
    </main>
  );
}

function SellerContactActions({ car, onMessage }) {
  const contact = car.sellerContact || {};
  const whatsapp = contact.whatsappPhone || contact.phone || car.sellerPhone;
  const phone = contact.phone || car.sellerPhone;
  const email = contact.email;
  return (
    <div className="mt-4 grid gap-3 sm:grid-cols-2">
      <button type="button" onClick={onMessage} className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#c1392a] px-4 py-3 text-sm font-bold text-dark hover:bg-[c1392a]"><MessageCircle className="h-4 w-4" />Message the seller</button>
      <div className="relative">
        <details className="group">
          <summary className="flex cursor-pointer list-none items-center justify-center gap-2 rounded-lg border border-border bg-white px-4 py-3 text-sm font-bold text-dark"><Phone className="h-4 w-4" />Call or WhatsApp</summary>
          <div className="absolute right-0 z-10 mt-2 w-full rounded-xl border border-border bg-white p-2 shadow-xl">
            {whatsapp && <a href={`https://wa.me/${String(whatsapp).replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm text-dark hover:bg-background"><MessageCircle className="h-4 w-4" />WhatsApp</a>}
            {phone && <a href={`tel:${phone}`} className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm text-dark hover:bg-background"><Phone className="h-4 w-4" />Call</a>}
            {email && <a href={`mailto:${email}`} className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm text-dark hover:bg-background"><Mail className="h-4 w-4" />Email</a>}
          </div>
        </details>
      </div>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <main className="min-h-[60vh] px-6 py-24 text-center text-muted">
      {message}
    </main>
  );
}

export default function CarDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [error, setError] = useState('');
  const [contactOpen, setContactOpen] = useState(false);
  const [shareLabel, setShareLabel] = useState('Share');

  async function shareListing() {
    const shareUrl = window.location.href;
    const name = [car?.make, car?.model].filter(Boolean).join(' ') || 'this vehicle';
    try {
      if (navigator.share) {
        await navigator.share({
          title: name,
          text: `Check out ${name} on My Car Kenya`,
          url: shareUrl,
        });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setShareLabel('Link copied');
      window.setTimeout(() => setShareLabel('Share'), 2200);
    } catch (shareError) {
      if (shareError.name !== 'AbortError') {
        setShareLabel('Unable to share');
        window.setTimeout(() => setShareLabel('Share'), 2200);
      }
    }
  }

  useEffect(() => {
    recordCarView(id).catch(() => {});
    fetchCar(id)
      .then(setCar)
      .catch((requestError) =>
        setError(requestError.message || 'Unable to load this vehicle.')
      );
  }, [id]);

  useEffect(() => {
    if (!car) return;
    const name = [car.make, car.model].filter(Boolean).join(' ') || 'Vehicle';
    const canonicalUrl = `https://mycarapp.co.ke/inventory/${car.id}`;
    const image = car.images?.[0] || 'https://mycarapp.co.ke/icon.png';
    updatePageMetadata({
      title: `${name}${car.year ? ` ${car.year}` : ''} for Sale in Kenya | My Car Kenya`,
      description: `${name}${car.year ? ` (${car.year})` : ''} listed for sale in ${car.sellerLocation || car.location || 'Kenya'}. View price, specifications, photos and seller details on My Car Kenya.`,
      keywords: `${name} Kenya, ${car.make || ''} ${car.model || ''} for sale, used cars Kenya, cars for sale Nairobi`,
      canonicalUrl,
      image,
      type: 'product',
      structuredData: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Vehicle',
            '@id': `${canonicalUrl}#vehicle`,
            name,
            url: canonicalUrl,
            image: car.images || [image],
            vehicleModelDate: car.year ? String(car.year) : undefined,
            manufacturer: car.make ? { '@type': 'Organization', name: car.make } : undefined,
            fuelType: car.fuelType || undefined,
            vehicleTransmission: car.transmission || undefined,
            mileageFromOdometer: car.mileage != null ? { '@type': 'QuantitativeValue', value: car.mileage, unitCode: 'KMT' } : undefined,
            offers: {
              '@type': 'Offer',
              url: canonicalUrl,
              price: car.price,
              priceCurrency: car.currency || 'KES',
              availability: 'https://schema.org/InStock',
              itemCondition: 'https://schema.org/UsedCondition',
            },
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://mycarapp.co.ke/' },
              { '@type': 'ListItem', position: 2, name: 'Vehicle inventory', item: 'https://mycarapp.co.ke/inventory' },
              { '@type': 'ListItem', position: 3, name, item: canonicalUrl },
            ],
          },
        ],
      },
    });
  }, [car]);

  if (error) return <ErrorState message={error} />;
  if (!car) return <LoadingState />;

  const images = car.images?.length
    ? car.images
    : [
        'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=1200&q=80',
      ];
  const name = [car.make, car.model].filter(Boolean).join(' ');
  const features = Array.isArray(car.features) ? car.features : [];

  return (
    <>
    <NavBar />
    <main className="bg-background px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
      <div className="mx-auto max-w-6xl">
        {/* Top bar */}
        <div className="mb-8 flex items-center justify-between">
          <Link
            to="/inventory"
            className="inline-flex items-center gap-2 text-sm font-semibold text-muted transition-colors hover:text-[#c1392a]"
          >
            <ArrowLeft className="h-4 w-4" /> Back to marketplace
          </Link>
          <button
            type="button"
            onClick={shareListing}
            aria-label="Share this listing"
            className="flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-muted shadow-sm transition-colors hover:border-[#c1392a] hover:text-[#c1392a]"
          >
            <Share2 className="h-4 w-4" /> {shareLabel}
          </button>
        </div>

        {/* Image + Summary */}
        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          <ImageGallery images={images} name={name} />
          <div><CarSummaryPanel car={car} name={name} />{(car.yardName || car.sellerLocation) && <div className="mt-4 rounded-2xl border border-border bg-white p-5"><p className="text-xs uppercase tracking-wide text-muted">Seller</p><p className="mt-1 font-semibold text-dark">{car.yardName || 'Verified seller'}</p>{car.sellerLocation && <p className="mt-1 flex items-center gap-1 text-sm text-muted"><MapPin className="h-4 w-4" />{car.sellerLocation}</p>}<SellerContactActions car={car} onMessage={() => getMarketplaceToken() ? setContactOpen(true) : navigate('/signup')} /></div>}</div>
        </div>

        {/* Details + Description + Features */}
        <div className="mt-10 grid gap-6">
          <VehicleDetailsPanel car={car} />
          <InspectionReportPanel report={car.inspectionReport} />
          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            <DescriptionPanel description={car.description} />
            <FeaturesPanel features={features} />
          </div>
        </div>
      </div>
    </main>
    <Footer />
    {contactOpen && <ContactSellerModal car={car} onClose={() => setContactOpen(false)} />}
    </>
  );
}